import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';

const CREATE_NEW_VALUE = '__create_new__';

export default function DimMakPointsBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [builderTab, setBuilderTab] = useState('new');

  const [meridians, setMeridians] = useState([]);
  const [selectedMeridianId, setSelectedMeridianId] = useState(null);
  const [points, setPoints] = useState([]);
  const [selectedPointId, setSelectedPointId] = useState(null);

  const [showMeridianDropdown, setShowMeridianDropdown] = useState(false);
  const [showPointDropdown, setShowPointDropdown] = useState(false);
  const [meridianMenuRect, setMeridianMenuRect] = useState(null);
  const [pointMenuRect, setPointMenuRect] = useState(null);
  const meridianTriggerRef = useRef(null);
  const pointTriggerRef = useRef(null);

  const [dimMakNumber, setDimMakNumber] = useState('');
  const [description, setDescription] = useState('');
  const [hotspot, setHotspot] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const styles = useMemo(() => getStyles(theme), [theme]);

  const selectedMeridian = meridians.find((m) => m.id === selectedMeridianId) || null;
  const selectedPoint = points.find((p) => p.id === selectedPointId) || null;
  const totalPoints = selectedMeridian?.total_dim_mak_points
    ? Number(selectedMeridian.total_dim_mak_points)
    : null;
  const atPointCap = totalPoints != null && totalPoints > 0 && points.length >= totalPoints;

  const fetchUser = useCallback(async (userId) => {
    try {
      const response = await fetch(`${getApiUrl()}/api/users/${userId}`);
      if (response.ok) setCurrentUser(await response.json());
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  }, []);

  const fetchMeridians = useCallback(async () => {
    try {
      const response = await fetch(`${getApiUrl()}/api/dim-mak-meridians`);
      if (response.ok) {
        const data = await response.json();
        setMeridians(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching meridians:', error);
    }
  }, []);

  const fetchPoints = useCallback(async (meridianId) => {
    if (!meridianId) {
      setPoints([]);
      return;
    }
    try {
      const response = await fetch(
        `${getApiUrl()}/api/dim-mak-meridians/${encodeURIComponent(String(meridianId))}/points`
      );
      if (response.ok) {
        const data = await response.json();
        setPoints(Array.isArray(data) ? data : []);
      } else {
        setPoints([]);
      }
    } catch (error) {
      console.error('Error fetching Dim Mak points:', error);
      setPoints([]);
    }
  }, []);

  useEffect(() => {
    const userId = route?.params?.userId || route?.params?.user?.id || 1;
    fetchUser(userId);
    fetchMeridians();
  }, [route, fetchUser, fetchMeridians]);

  useEffect(() => {
    fetchPoints(selectedMeridianId);
  }, [selectedMeridianId, fetchPoints]);

  const nextNumber = useMemo(() => {
    if (!points.length) return 1;
    return Math.max(...points.map((p) => Number(p.dim_mak_number) || 0)) + 1;
  }, [points]);

  const resetPointFields = useCallback(
    (forNew = true) => {
      setSelectedPointId(null);
      setDimMakNumber(forNew ? String(nextNumber) : '');
      setDescription('');
      setHotspot('');
      setSaveStatus(null);
      setErrorMessage('');
      setShowPointDropdown(false);
      setPointMenuRect(null);
    },
    [nextNumber]
  );

  const populatePoint = useCallback((point) => {
    setSelectedPointId(point.id);
    setDimMakNumber(String(point.dim_mak_number));
    setDescription(point.dim_mak_point_description || '');
    setHotspot(point.hotspot || '');
    setBuilderTab('edit');
    setSaveStatus(null);
    setErrorMessage('');
    setShowPointDropdown(false);
    setPointMenuRect(null);
  }, []);

  const switchTab = (tab) => {
    if (tab === builderTab) return;
    setBuilderTab(tab);
    setSaveStatus(null);
    setErrorMessage('');
    if (tab === 'new') {
      setSelectedPointId(null);
      setDimMakNumber(selectedMeridianId ? String(nextNumber) : '');
      setDescription('');
      setHotspot('');
    }
  };

  const closeMeridianDropdown = useCallback(() => {
    setShowMeridianDropdown(false);
    setMeridianMenuRect(null);
  }, []);

  const closePointDropdown = useCallback(() => {
    setShowPointDropdown(false);
    setPointMenuRect(null);
  }, []);

  const openMeasuredMenu = useCallback((ref, setRect, setOpen) => {
    if (Platform.OS === 'web' && ref.current?.measureInWindow) {
      ref.current.measureInWindow((x, y, width, height) => {
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
        const top = y + height + 4;
        const maxHeight = Math.max(120, Math.min(280, viewportH - top - 16));
        setRect({
          top,
          left: x,
          width: Math.max(width, 200),
          maxHeight,
        });
        setOpen(true);
      });
      return;
    }
    setOpen(true);
  }, []);

  const handleMeridianSelect = (row) => {
    setSelectedMeridianId(row.id);
    closeMeridianDropdown();
    closePointDropdown();
    setSelectedPointId(null);
    setDimMakNumber('');
    setDescription('');
    setHotspot('');
    setSaveStatus(null);
    setErrorMessage('');
    setBuilderTab('new');
  };

  const handlePointDropdownSelect = (value) => {
    closePointDropdown();
    if (value === CREATE_NEW_VALUE) {
      setBuilderTab('new');
      setSelectedPointId(null);
      setDimMakNumber(String(nextNumber));
      setDescription('');
      setHotspot('');
      setSaveStatus(null);
      setErrorMessage('');
      return;
    }
    const point = points.find((p) => Number(p.dim_mak_number) === Number(value));
    if (point) populatePoint(point);
  };

  useEffect(() => {
    if (builderTab === 'new' && selectedMeridianId && !selectedPointId) {
      setDimMakNumber((current) => current || String(nextNumber));
    }
  }, [builderTab, selectedMeridianId, selectedPointId, nextNumber]);

  const savePoint = async () => {
    if (!selectedMeridianId) {
      setSaveStatus('error');
      setErrorMessage('Select a meridian first.');
      return;
    }
    if (!/^\d+$/.test(String(dimMakNumber).trim()) || Number(dimMakNumber) < 1) {
      setSaveStatus('error');
      setErrorMessage('Dim Mak number is required and must be a positive number.');
      return;
    }
    if (builderTab === 'edit' && !selectedPointId) {
      setSaveStatus('error');
      setErrorMessage('Select a Dim Mak point to edit.');
      return;
    }

    if (saving) return;
    setSaving(true);
    setSaveStatus(null);
    setErrorMessage('');

    try {
      const payload = {
        dim_mak_meridian_id: selectedMeridianId,
        dim_mak_number: Number(dimMakNumber),
        dim_mak_point_description: description,
        hotspot: hotspot.trim() || null,
      };
      const url =
        builderTab === 'edit'
          ? `${getApiUrl()}/api/dim-mak-points/${selectedPointId}`
          : `${getApiUrl()}/api/dim-mak-points`;
      const response = await fetch(url, {
        method: builderTab === 'edit' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setSaveStatus('error');
        setErrorMessage(data.error || 'Failed to save Dim Mak point.');
        return;
      }
      setSaveStatus('success');
      await fetchPoints(selectedMeridianId);
      if (builderTab === 'edit' && data?.id) {
        populatePoint(data);
        setSaveStatus('success');
      }
    } catch (error) {
      console.error('Error saving Dim Mak point:', error);
      setSaveStatus('error');
      setErrorMessage('Failed to save Dim Mak point. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const pointDropdownLabel = selectedPoint
    ? String(selectedPoint.dim_mak_number)
    : builderTab === 'new'
      ? 'Create new Dim Mak point'
      : 'Select a point';

  const meridianMenuReady = Platform.OS !== 'web' || meridianMenuRect != null;
  const pointMenuReady = Platform.OS !== 'web' || pointMenuRect != null;

  const meridianMenuList = (
    <ScrollView
      style={[
        styles.dropdownScroll,
        Platform.OS === 'web' && meridianMenuRect ? { maxHeight: meridianMenuRect.maxHeight } : null,
      ]}
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      {...(Platform.OS === 'web' ? { dataSet: { dimMakPointsDropdownScroll: 'true' } } : {})}
    >
      {meridians.map((row) => (
        <TouchableOpacity
          key={row.id}
          style={styles.dropdownRow}
          onPress={() => handleMeridianSelect(row)}
          {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
        >
          <Text style={styles.dropdownRowText}>{row.meridian_name}</Text>
        </TouchableOpacity>
      ))}
      {meridians.length === 0 ? (
        <Text style={styles.dropdownEmpty}>No meridians created yet.</Text>
      ) : null}
    </ScrollView>
  );

  const pointMenuList = (
    <ScrollView
      style={[
        styles.dropdownScroll,
        Platform.OS === 'web' && pointMenuRect ? { maxHeight: pointMenuRect.maxHeight } : null,
      ]}
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      {...(Platform.OS === 'web' ? { dataSet: { dimMakPointsDropdownScroll: 'true' } } : {})}
    >
      {points.map((point) => (
        <TouchableOpacity
          key={point.id}
          style={styles.dropdownRow}
          onPress={() => handlePointDropdownSelect(point.dim_mak_number)}
          {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
        >
          <Text style={styles.dropdownRowText}>{point.dim_mak_number}</Text>
        </TouchableOpacity>
      ))}
      {!atPointCap ? (
        <TouchableOpacity
          style={[styles.dropdownRow, styles.createNewRow]}
          onPress={() => handlePointDropdownSelect(CREATE_NEW_VALUE)}
          {...(Platform.OS === 'web'
            ? { dataSet: { dropdownRow: 'true', dimMakCreatePoint: 'true' } }
            : {})}
        >
          <Text style={styles.createNewText}>Create new Dim Mak point</Text>
        </TouchableOpacity>
      ) : null}
      {points.length === 0 && atPointCap ? (
        <Text style={styles.dropdownEmpty}>No points available.</Text>
      ) : null}
    </ScrollView>
  );

  const meridianMenuNode =
    showMeridianDropdown && meridianMenuReady ? (
      <>
        {Platform.OS === 'web' ? (
          <TouchableOpacity
            style={styles.dropdownOverlay}
            activeOpacity={1}
            onPress={closeMeridianDropdown}
            {...(Platform.OS === 'web' ? { dataSet: { dimMakPointsDropdownOverlay: 'true' } } : {})}
          />
        ) : null}
        <View
          style={[
            styles.dropdownList,
            Platform.OS === 'web' && meridianMenuRect
              ? {
                  position: 'fixed',
                  top: meridianMenuRect.top,
                  left: meridianMenuRect.left,
                  width: meridianMenuRect.width,
                  right: undefined,
                  marginTop: 0,
                  maxHeight: meridianMenuRect.maxHeight,
                }
              : null,
          ]}
          {...(Platform.OS === 'web'
            ? { dataSet: { dropdownSurface: 'true', dimMakPointsDropdownMenu: 'true' } }
            : {})}
        >
          {meridianMenuList}
        </View>
      </>
    ) : null;

  const pointMenuNode =
    showPointDropdown && pointMenuReady ? (
      <>
        {Platform.OS === 'web' ? (
          <TouchableOpacity
            style={styles.dropdownOverlay}
            activeOpacity={1}
            onPress={closePointDropdown}
            {...(Platform.OS === 'web' ? { dataSet: { dimMakPointsDropdownOverlay: 'true' } } : {})}
          />
        ) : null}
        <View
          style={[
            styles.dropdownList,
            Platform.OS === 'web' && pointMenuRect
              ? {
                  position: 'fixed',
                  top: pointMenuRect.top,
                  left: pointMenuRect.left,
                  width: pointMenuRect.width,
                  right: undefined,
                  marginTop: 0,
                  maxHeight: pointMenuRect.maxHeight,
                }
              : null,
          ]}
          {...(Platform.OS === 'web'
            ? { dataSet: { dropdownSurface: 'true', dimMakPointsDropdownMenu: 'true' } }
            : {})}
        >
          {pointMenuList}
        </View>
      </>
    ) : null;

  const portaledMenus =
    Platform.OS === 'web' &&
    (meridianMenuNode || pointMenuNode) &&
    typeof document !== 'undefined'
      ? require('react-dom').createPortal(
          <div
            data-dim-mak-points-dropdown-portal="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100000,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
              {meridianMenuNode}
              {pointMenuNode}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'dim-mak-points-builder' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Dim Mak Meridian points</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.titleSection}>
          <Text style={styles.title}>Dim Mak Meridian points</Text>
          <Text style={styles.subtitle}>
            Select a meridian, then edit an existing point number or create a new Dim Mak point.
          </Text>
        </View>

        <View style={styles.form}>
          <View style={styles.pageTabRow}>
            <TouchableOpacity
              style={[styles.pageTab, builderTab === 'new' && styles.pageTabOn]}
              onPress={() => switchTab('new')}
              disabled={saving}
            >
              <Text style={[styles.pageTabText, builderTab === 'new' && styles.pageTabTextOn]}>New</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.pageTab, builderTab === 'edit' && styles.pageTabOn]}
              onPress={() => switchTab('edit')}
              disabled={saving}
            >
              <Text style={[styles.pageTabText, builderTab === 'edit' && styles.pageTabTextOn]}>Edit</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Meridian</Text>
            <View ref={meridianTriggerRef} collapsable={false}>
              <TouchableOpacity
                style={styles.dropdownTrigger}
                onPress={() => {
                  closePointDropdown();
                  if (showMeridianDropdown) closeMeridianDropdown();
                  else openMeasuredMenu(meridianTriggerRef, setMeridianMenuRect, setShowMeridianDropdown);
                }}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text style={styles.dropdownTriggerText}>
                  {selectedMeridian?.meridian_name || 'Select a meridian'}
                </Text>
                <Text style={styles.dropdownChevron}>{showMeridianDropdown ? '▲' : '▼'}</Text>
              </TouchableOpacity>
            </View>
            {Platform.OS !== 'web' ? meridianMenuNode : null}
          </View>

          {selectedMeridian ? (
            <View style={styles.fieldContainer}>
              <Text style={styles.label}>Dim Mak point</Text>
              <View ref={pointTriggerRef} collapsable={false}>
                <TouchableOpacity
                  style={styles.dropdownTrigger}
                  onPress={() => {
                    closeMeridianDropdown();
                    if (showPointDropdown) closePointDropdown();
                    else openMeasuredMenu(pointTriggerRef, setPointMenuRect, setShowPointDropdown);
                  }}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text style={styles.dropdownTriggerText}>{pointDropdownLabel}</Text>
                  <Text style={styles.dropdownChevron}>{showPointDropdown ? '▲' : '▼'}</Text>
                </TouchableOpacity>
              </View>
              {Platform.OS !== 'web' ? pointMenuNode : null}
              {totalPoints != null ? (
                <Text style={styles.helpText}>
                  {points.length} of {totalPoints} points created
                  {atPointCap ? ' — create new is hidden because the total has been reached.' : ''}.
                </Text>
              ) : null}
            </View>
          ) : null}

          {saveStatus && (
            <View
              style={[
                styles.statusBox,
                saveStatus === 'success' ? styles.successBox : styles.errorBox,
              ]}
            >
              <Text
                style={[
                  styles.statusBoxTitle,
                  saveStatus === 'success' ? styles.successBoxText : styles.errorBoxText,
                ]}
              >
                {saveStatus === 'success' ? 'Record saved successfully!' : 'Error'}
              </Text>
              {saveStatus === 'error' && errorMessage ? (
                <Text style={styles.errorMessage}>{errorMessage}</Text>
              ) : null}
              {saveStatus === 'success' ? (
                <View style={styles.statusButtons}>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.backButton]}
                    onPress={() =>
                      navigation.navigate('DimMakBuilder', {
                        userId: currentUser?.id || 1,
                        user: currentUser,
                      })
                    }
                  >
                    <Text style={styles.statusButtonText}>Back to Dim mak builder</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.statusButton, styles.resetStatusButton]}
                    onPress={() => resetPointFields(builderTab === 'new')}
                  >
                    <Text style={styles.statusButtonText}>Reset form</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <TouchableOpacity
                  style={[styles.statusButton, styles.resetStatusButton]}
                  onPress={() => {
                    setSaveStatus(null);
                    setErrorMessage('');
                  }}
                >
                  <Text style={styles.statusButtonText}>Try Again</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {!saveStatus && selectedMeridian && (builderTab === 'new' || selectedPointId) ? (
            <>
              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Dim Mak number *</Text>
                <TextInput
                  style={styles.input}
                  value={dimMakNumber}
                  onChangeText={setDimMakNumber}
                  placeholder="1"
                  placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                  keyboardType="number-pad"
                />
              </View>

              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Dim Mak point description</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  value={description}
                  onChangeText={setDescription}
                  placeholder="Long description of this point"
                  placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                  multiline
                  numberOfLines={8}
                  textAlignVertical="top"
                />
              </View>

              <View style={styles.fieldContainer}>
                <Text style={styles.label}>Hotspot</Text>
                <Text style={styles.helpText}>Location on the meridian image.</Text>
                <TextInput
                  style={styles.input}
                  value={hotspot}
                  onChangeText={setHotspot}
                  placeholder="e.g. 42,68"
                  placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                />
              </View>

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.resetButton]}
                  onPress={() => resetPointFields(builderTab === 'new')}
                >
                  <Text style={styles.buttonText}>Reset</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.button, styles.saveButton, saving && styles.saveButtonDisabled]}
                  onPress={savePoint}
                  disabled={saving}
                >
                  <Text style={styles.buttonText}>{saving ? 'Saving…' : 'Save'}</Text>
                </TouchableOpacity>
              </View>
            </>
          ) : null}
        </View>
      </ScrollView>
      {portaledMenus}
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: 'transparent',
    },
    hamburgerContainer: { marginRight: 15, backgroundColor: 'transparent' },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: { backgroundColor: 'transparent' },
    content: { flex: 1 },
    contentContainer: { padding: 20, paddingBottom: 40 },
    titleSection: { marginBottom: 24, alignItems: 'center' },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      color: theme.isDarkMode ? '#FDFD0A' : '#000000',
      marginBottom: 10,
      textAlign: 'center',
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 14,
      fontStyle: 'italic',
      color: theme.colors.text,
      textAlign: 'center',
      lineHeight: 20,
      opacity: 0.8,
      backgroundColor: 'transparent',
    },
    form: { width: '100%', maxWidth: 600, alignSelf: 'center' },
    pageTabRow: {
      flexDirection: 'row',
      gap: 8,
      marginBottom: 24,
      alignSelf: 'stretch',
    },
    pageTab: {
      flex: 1,
      paddingVertical: 12,
      borderRadius: 8,
      alignItems: 'center',
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
    },
    pageTabOn: { backgroundColor: theme.colors.primary || '#d4af37' },
    pageTabText: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    pageTabTextOn: { color: '#000', backgroundColor: 'transparent' },
    fieldContainer: { marginBottom: 20 },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    helpText: {
      fontSize: 12,
      color: theme.colors.text,
      opacity: 0.7,
      marginTop: 8,
      fontStyle: 'italic',
      backgroundColor: 'transparent',
    },
    input: {
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    textArea: { minHeight: 160, textAlignVertical: 'top' },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    dropdownTriggerText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
      flex: 1,
    },
    dropdownChevron: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
      marginLeft: 8,
    },
    dropdownList: {
      marginTop: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      maxHeight: 280,
      overflow: 'hidden',
      alignSelf: 'stretch',
      zIndex: 100002,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 4px 16px rgba(0, 0, 0, 0.28)' }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 12,
          }),
    },
    dropdownScroll: {
      maxHeight: 280,
    },
    dropdownOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'transparent',
      zIndex: 100001,
    },
    dropdownRow: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      alignSelf: 'stretch',
      width: '100%',
    },
    dropdownRowText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownEmpty: {
      padding: 12,
      fontSize: 14,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    createNewRow: {
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#F0F0F0',
    },
    createNewText: {
      fontSize: 16,
      color: theme.colors.primary || '#d4af37',
      fontWeight: '600',
      backgroundColor: 'transparent',
    },
    buttonContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginTop: 12,
      gap: 15,
    },
    button: {
      flex: 1,
      padding: 15,
      borderRadius: 8,
      alignItems: 'center',
    },
    resetButton: { backgroundColor: theme.isDarkMode ? '#555' : '#9e9e9e' },
    saveButton: { backgroundColor: theme.colors.primary || '#d4af37' },
    saveButtonDisabled: { opacity: 0.6 },
    buttonText: {
      color: '#000',
      fontSize: 16,
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    statusBox: {
      borderRadius: 8,
      padding: 16,
      marginBottom: 20,
    },
    successBox: { backgroundColor: theme.isDarkMode ? '#1b3d1b' : '#E8F5E9' },
    errorBox: { backgroundColor: theme.isDarkMode ? '#4a2020' : '#FFEBEE' },
    statusBoxTitle: {
      fontSize: 16,
      fontWeight: '700',
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    successBoxText: { color: theme.isDarkMode ? '#81c784' : '#2E7D32' },
    errorBoxText: { color: theme.isDarkMode ? '#ff8a80' : '#C62828' },
    errorMessage: {
      fontSize: 14,
      color: theme.isDarkMode ? '#ff8a80' : '#C62828',
      backgroundColor: 'transparent',
      marginBottom: 8,
    },
    statusButtons: { flexDirection: 'row', gap: 10, marginTop: 8 },
    statusButton: {
      flex: 1,
      padding: 12,
      borderRadius: 8,
      alignItems: 'center',
    },
    backButton: { backgroundColor: theme.colors.primary || '#d4af37' },
    resetStatusButton: { backgroundColor: theme.isDarkMode ? '#555' : '#9e9e9e' },
    statusButtonText: {
      color: '#000',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
  });
